import { Alert, Button, Flex, FormField, Heading, Modal, Text } from '@nrx/ui';
import { useState, type FormEvent, type JSX } from 'react';

import { formatUsPhoneMask } from '../../utils/us-phone-input-mask.js';
import type { ConsultMemberDetails } from '../exchange/exchange-types.js';
import type { GroupConsultEvent } from '../group-consults/group-consult-calendar.js';
import { formatEventTimeRangeCompact } from '../group-consults/group-consult-calendar.js';

const TITLE_ID = 'register-modal-title';

type RegisterWebinarModalProps = {
  webinarId: string;
  /** The session being registered for; absent when the list no longer holds it. */
  event: GroupConsultEvent | undefined;
  timeZone: string;
  /** When present, name and email are pre-filled AND locked (the link identified the member). */
  memberDetails: ConsultMemberDetails | null;
  phoneDisplay: string;
  onPhoneDisplayChange: (display: string) => void;
  registerError: string | null;
  registerLoading: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>, webinarId: string) => void;
  onCancel: () => void;
};

/**
 * Webinar registration modal — confirms session time; name/email from link when provided.
 *
 * @remarks W4: this is a real modal, so it is the library `Modal`, which owns the labelled
 * `role="dialog"`, the focus trap, Escape-to-close, body scroll lock and focus restore. None of
 * that existed before — the vendor hand-rolled a fixed-position div with `role="dialog"` and no
 * keyboard handling whatsoever.
 *
 * The three identity fields became CONTROLLED, because `FormField` deliberately omits
 * `defaultValue` from its prop type. `useWebinarRegistration` still reads the submitted values off
 * the form with `form.elements.namedItem('register-…')`, which behaves identically for a
 * controlled input, so the `name` attributes below are load-bearing and must not be renamed.
 * Per-session reset now comes from `key={registeringWebinarId}` at the call site, replacing the
 * `key` that used to sit on the `<form>`: with the values in local state, remounting the form
 * subtree alone would no longer clear them.
 *
 * `Button busy` rather than a hand-rolled `disabled` + spinner: it sets `aria-busy` and forces
 * `disabled` from one prop (W4).
 */
export function RegisterWebinarModal({
  webinarId,
  event,
  timeZone,
  memberDetails,
  phoneDisplay,
  onPhoneDisplayChange,
  registerError,
  registerLoading,
  onSubmit,
  onCancel,
}: RegisterWebinarModalProps): JSX.Element {
  const fieldsLocked = Boolean(memberDetails);
  const [email, setEmail] = useState(memberDetails?.email ?? '');
  const [firstName, setFirstName] = useState(memberDetails?.firstName ?? '');
  const [lastName, setLastName] = useState(memberDetails?.lastName ?? '');

  return (
    <Modal open onClose={onCancel} aria-labelledby={TITLE_ID}>
      <Heading as="h2" id={TITLE_ID} size="5">
        Register for session
      </Heading>
      {event && (
        <Flex direction="column" gap="1" className="consult-modal__summary">
          <Text as="p" size="2" weight="medium">
            {event.title}
          </Text>
          <Text as="p" size="2" color="muted">
            <Text size="2" weight="medium">
              Date and time:{' '}
            </Text>
            {formatEventTimeRangeCompact(event.start, event.end, timeZone)}
          </Text>
        </Flex>
      )}
      <form
        onSubmit={(e) => {
          onSubmit(e, webinarId);
        }}
      >
        <Flex direction="column" gap="4">
          <FormField
            label="Email"
            id="register-email"
            name="register-email"
            type="email"
            required={!fieldsLocked}
            autoComplete="email"
            disabled={fieldsLocked}
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            className="consult-field"
          />
          <FormField
            label="First name"
            id="register-firstName"
            name="register-firstName"
            required={!fieldsLocked}
            autoComplete="given-name"
            disabled={fieldsLocked}
            value={firstName}
            onChange={setFirstName}
            className="consult-field"
          />
          <FormField
            label="Last name"
            id="register-lastName"
            name="register-lastName"
            required={!fieldsLocked}
            autoComplete="family-name"
            disabled={fieldsLocked}
            value={lastName}
            onChange={setLastName}
            className="consult-field"
          />
          <FormField
            label="Phone"
            id="register-phone"
            name="register-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            maxLength={14}
            value={phoneDisplay}
            onChange={(next) => onPhoneDisplayChange(formatUsPhoneMask(next))}
            placeholder="(555) 123-4567"
            help="Enter 10 digits; formatting is added automatically."
            className="consult-field"
          />
          {registerError && <Alert tone="danger">{registerError}</Alert>}
        </Flex>
        <Modal.Footer>
          <Button tone="secondary" onClick={onCancel} className="consult-modal__action">
            Cancel
          </Button>
          <Button type="submit" busy={registerLoading} className="consult-modal__action">
            {registerLoading ? 'Registering…' : 'Register'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
